import React, { useEffect, useMemo, useRef, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabComponent, TabItemDirective, TabItemsDirective } from '@syncfusion/ej2-react-navigations';
import { GridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-grids';
import { useWizardContext } from '../../contexts/useWizardContext';
import type { DoughBatch, Campaign } from '../../contexts/ProductionScheduleWizardContext';
import LongTermBakeOutTab from './LongTermBakeOutTab';
import { buildIntermediateOrders } from '../../utils/buildIntermediateOrders';

const TAB_HEADER_HEIGHT_PX = 48;
const NAME_FIELD_HEIGHT_PX = 68;

interface ReviewItem {
  sequenceNumber: number; // From campaign.sequenceNumber (sequence order)
  productName: string;
  campaignId: string;
  batchSequenceNumber: number; // sequenceNumber within the batch
  quantity: number; // piece count
  doughQuantity: number; // dough quantity in kg (doughQuantityKg)
}

const Step4Review: React.FC = () => {
  const { t } = useTranslation(['common']);
  const {
    batchDeterminationData,
    sequenceOptimization,
    forecasts,
    pendingForecasts,
    materials,
    formData,
    updateFormData,
    generateDefaultScheduleName,
    getPreviewScheduleUuid,
  } = useWizardContext();

  const [scheduleName, setScheduleName] = useState(() =>
    formData.scheduleName ?? generateDefaultScheduleName()
  );
  const scheduleNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!formData.scheduleName) {
      updateFormData('scheduleName', scheduleName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Syncfusion dialog renders the body outside React's event root, so React synthetic
  // events (onChange) do not fire on a plain <input>. We attach a native 'input' listener
  // so that typing really updates the state + formData.
  useEffect(() => {
    const inputEl = scheduleNameInputRef.current;
    if (!inputEl) return;
    const handleInput = () => {
      const value = inputEl.value;
      setScheduleName(value);
      updateFormData('scheduleName', value);
    };
    inputEl.addEventListener('input', handleInput);
    return () => inputEl.removeEventListener('input', handleInput);
  }, [updateFormData]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gridHeightPx, setGridHeightPx] = useState(320);
  const lastGridHeightRef = useRef(320);

  // Compute the available grid height the same way as ScheduleViewContent (production schedules view)
  useLayoutEffect(() => {
    let rafId: number | null = null;
    const updateGridHeight = () => {
      const containerEl = containerRef.current;
      if (!containerEl) return;
      const containerHeight = containerEl.clientHeight;
      if (!containerHeight) return;
      // Tab-Header, Padding, Beschreibungsparagraph
      const newHeight = Math.max(200, Math.floor(containerHeight - TAB_HEADER_HEIGHT_PX - NAME_FIELD_HEIGHT_PX - 56 - 32));
      if (newHeight !== lastGridHeightRef.current) {
        lastGridHeightRef.current = newHeight;
        setGridHeightPx(newHeight);
      }
    };
    const scheduleUpdate = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateGridHeight();
      });
    };
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    const containerEl = containerRef.current;
    if (containerEl) resizeObserver.observe(containerEl);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, []);

  // Collect all dough batches from all materials and sort them by sequence order
  const ovenCapacityWarnings = useMemo(() => {
    const warnings: Array<{ campaignId: string; ovenBatches: number; availableOvens: number }> = [];
    Object.values(batchDeterminationData).forEach((batchData: any) => {
      if (batchData.ovenCapacityWarnings) {
        warnings.push(...batchData.ovenCapacityWarnings);
      }
    });
    return warnings;
  }, [batchDeterminationData]);

  const reviewData = useMemo(() => {
    const items: ReviewItem[] = [];
    
    // Collect all campaigns with their dough batches
    const allCampaignsWithBatches: Array<{
      campaign: Campaign;
      doughBatch: DoughBatch;
    }> = [];
    
    // Iterate over all materials in batchDeterminationData
    Object.values(batchDeterminationData).forEach((batchData: any) => {
      if (!batchData.campaigns || batchData.campaigns.length === 0) {
        return; // skip materials without campaigns
      }
      
      // For every campaign of this material
      batchData.campaigns.forEach((campaign: any) => {
        // For every dough batch of this campaign
        campaign.doughBatches.forEach((doughBatch: any) => {
          allCampaignsWithBatches.push({
            campaign,
            doughBatch
          });
        });
      });
    });
    
    // Sort: sequenceNumber -> campaignId -> doughBatch.sequenceNumber.
    // campaignId is the second key so that, when two campaigns share the same sequenceNumber
    // (e.g. an oven split produced campaign _1 and _2 within the same sequence slot), all
    // batches of campaign _1 come first, then all batches of campaign _2 - instead of
    // interleaving batch 1 of _1, batch 1 of _2, batch 2 of _1, ... which is hard to read.
    // For sequence optimization (all sequenceNumber = 0): sort by product name first.
    allCampaignsWithBatches.sort((a, b) => {
      if (sequenceOptimization) {
        const nameDiff = (a.campaign.materialName || '').localeCompare(b.campaign.materialName || '');
        if (nameDiff !== 0) return nameDiff;
        const campaignIdDiff = (a.campaign.campaignId || '').localeCompare(b.campaign.campaignId || '');
        if (campaignIdDiff !== 0) return campaignIdDiff;
        return (a.doughBatch.sequenceNumber || 0) - (b.doughBatch.sequenceNumber || 0);
      }
      const sequenceDiff = (a.campaign.sequenceNumber || 0) - (b.campaign.sequenceNumber || 0);
      if (sequenceDiff !== 0) return sequenceDiff;
      const campaignIdDiff = (a.campaign.campaignId || '').localeCompare(b.campaign.campaignId || '');
      if (campaignIdDiff !== 0) return campaignIdDiff;
      return (a.doughBatch.sequenceNumber || 0) - (b.doughBatch.sequenceNumber || 0);
    });
    
    // Convert to ReviewItem
    allCampaignsWithBatches.forEach(({ campaign, doughBatch }) => {
      items.push({
        sequenceNumber: campaign.sequenceNumber || 0, // sequenceNumber from the sequence entry
        productName: campaign.materialName || '',
        campaignId: campaign.campaignId || '',
        batchSequenceNumber: doughBatch.sequenceNumber || 0,
        quantity: doughBatch.quantity || 0,
        doughQuantity: doughBatch.doughQuantityKg || 0 // doughQuantityKg instead of doughQuantity
      });
    });
    
    return items;
  }, [batchDeterminationData, sequenceOptimization]);

  const gridColumns = useMemo(() => [
    ...(!sequenceOptimization ? [{
      field: 'sequenceNumber',
      headerText: t('productionSchedule.wizard.step4.sequenceNumber', 'Sequenz-Nr.'),
      width: 100,
      textAlign: 'Right' as const
    }] : []),
    {
      field: 'productName',
      headerText: t('productionSchedule.wizard.step4.product', 'Produkt'),
      width: 200
    },
    {
      field: 'campaignId',
      headerText: t('productionSchedule.wizard.step4.campaignId', 'Kampagne'),
      width: 150
    },
    {
      field: 'batchSequenceNumber',
      headerText: t('productionSchedule.wizard.step4.batchSequenceNumber', 'Charge'),
      width: 80,
      textAlign: 'Right' as const
    },
    {
      field: 'quantity',
      headerText: t('productionSchedule.wizard.step4.quantity', 'Stückzahl'),
      width: 100,
      textAlign: 'Right' as const,
      format: 'n0'
    },
    {
      field: 'doughQuantity',
      headerText: t('productionSchedule.wizard.step4.doughQuantity', 'Teigmenge (kg)'),
      width: 120,
      textAlign: 'Right' as const,
      format: 'n2'
    }
  ], [t, sequenceOptimization]);

  const [selectedTab, setSelectedTab] = useState(0);

  // Intermediate product orders for the display in this tab. The save path
  // (ProductionScheduleWizardDialog.handleSave) calls buildIntermediateOrders again independently -
  // a deliberate double computation, because mirroring it into the wizard context through useEffect
  // triggered a render loop (the memo dependency getPreviewScheduleUuid was not stable).
  const intermediateOrderRows = useMemo(() => {
    const planStart = formData.plannedStart?.split('T')[0] ?? null;
    const planEnd = formData.plannedEnd?.split('T')[0] ?? planStart;
    const schedulePrefix = (getPreviewScheduleUuid?.() ?? '').replace(/-/g, '').substring(0, 8);
    const allForecasts = [...(forecasts ?? []), ...(pendingForecasts ?? [])];
    return buildIntermediateOrders({
      forecasts: allForecasts,
      materials: materials as any,
      planStart,
      planEnd,
      schedulePrefix,
    }).rows;
  }, [forecasts, pendingForecasts, materials, formData.plannedStart, formData.plannedEnd, getPreviewScheduleUuid]);

  return (
    <div ref={containerRef} className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
          {t('productionSchedule.wizard.step4.scheduleName', 'Name')}
        </label>
        <input
          ref={scheduleNameInputRef}
          type="text"
          defaultValue={scheduleName}
          placeholder={t('productionSchedule.wizard.step4.scheduleNamePlaceholder', 'Name des Produktionsplans')}
          className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <TabComponent
        cssClass="wizard-step4-tabs wizard-step4-tabs--headers-only"
        selectedItem={selectedTab}
        selected={(e: any) => { if (e.selectedIndex !== undefined) setSelectedTab(e.selectedIndex); }}
      >
        <TabItemsDirective>
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step4.tab.newBatches', 'Neue Chargen') }} />
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step4.tab.bakeOut', 'Ausbacken') }} />
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step4.tab.intermediates', 'Vorprodukte') }} />
        </TabItemsDirective>
      </TabComponent>

      <div className="flex flex-col flex-1 min-h-0 p-4" style={{ display: selectedTab === 0 ? 'flex' : 'none' }}>
        <p className="text-sm text-gray-600 mb-2">
          {sequenceOptimization
            ? t('productionSchedule.wizard.step4.reviewDescriptionAuto', 'Die Reihenfolge wird beim Einplanen automatisch optimiert.')
            : t('productionSchedule.wizard.step4.reviewDescription', 'Übersicht über die Sequenz und die zugeordneten Mengen')}
        </p>
        {ovenCapacityWarnings.length > 0 && (
          <div className="mb-3 rounded border border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
            <strong>{t('productionSchedule.wizard.step4.ovenCapacityWarningTitle', 'Hinweis: Ofen-Überkapazität')}</strong>
            <ul className="mt-1 list-disc pl-4">
              {ovenCapacityWarnings.map((w, i) => (
                <li key={i}>
                  {t(
                    'productionSchedule.wizard.step4.ovenCapacityWarningItem',
                    'Kampagne {{campaignId}}: {{ovenBatches}} Ofen-Chargen, aber nur {{availableOvens}} Öfen verfügbar. Ein automatischer Split war nicht möglich – der Plan wird trotzdem gespeichert.',
                    { campaignId: w.campaignId, ovenBatches: w.ovenBatches, availableOvens: w.availableOvens }
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <GridComponent
          dataSource={reviewData}
          height={gridHeightPx}
          allowPaging={false}
          allowSorting={true}
          locale="de-DE"
        >
          <ColumnsDirective>
            {gridColumns.map((col, idx) => (
              <ColumnDirective
                key={idx}
                field={col.field}
                headerText={col.headerText}
                width={col.width}
                textAlign={col.textAlign}
                format={col.format}
              />
            ))}
          </ColumnsDirective>
        </GridComponent>
      </div>

      {selectedTab === 1 && (
        <div className="flex flex-col flex-1 min-h-0 p-4">
          <LongTermBakeOutTab gridHeightPx={gridHeightPx} />
        </div>
      )}

      <div className="flex flex-col flex-1 min-h-0 p-4" style={{ display: selectedTab === 2 ? 'flex' : 'none' }}>
        {intermediateOrderRows.length === 0 ? (
          <div className="text-sm text-gray-500 italic">
            {t('productionSchedule.wizard.step4.intermediatesEmpty', 'Keine Vorprodukt-Aufträge geplant.')}
          </div>
        ) : (
          <GridComponent
            dataSource={intermediateOrderRows}
            height={gridHeightPx}
            allowPaging={false}
            allowSorting={true}
            locale="de-DE"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="productName"
                headerText={t('productionSchedule.wizard.step4.intermediates.col.product', 'Vorprodukt')}
                width={220}
              />
              <ColumnDirective
                field="orderId"
                headerText={t('productionSchedule.wizard.step4.intermediates.col.orderId', 'Auftrags-ID')}
                width={260}
              />
              <ColumnDirective
                field="lotSize"
                headerText={t('productionSchedule.wizard.step4.intermediates.col.lotSize', 'Losgröße')}
                width={140}
                textAlign="Right"
                format="n3"
              />
              <ColumnDirective
                field="baseUnit"
                headerText={t('productionSchedule.wizard.step4.intermediates.col.unit', 'Einheit')}
                width={100}
              />
            </ColumnsDirective>
          </GridComponent>
        )}
      </div>
    </div>
  );
};

export default Step4Review;
